// frontend/src/pages/ExploreCircleDetailsPreview.js

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarBlank,
  CheckCircle,
  Fire,
  Target,
  LockSimpleOpen,
  MapPin,
  Megaphone,
  PushPin,
  Handshake,
} from "phosphor-react";

import PreLoginHeader from "../components/PreLoginHeader";
import ActivityCard from "../components/ActivityCard";
import AvatarStack from "../components/AvatarStack";
import AboutPage from "./AboutPage";

/* -----------------------------------------------------------
   DEMO HELPERS
------------------------------------------------------------ */

const hoursAgo = (hours) => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

/* -----------------------------------------------------------
   DEMO USERS
------------------------------------------------------------ */

const DEMO_USERS = {
  jade: {
    id: 9001,
    name: "Jade Kim",
    display_name: "Jade Kim",
    username: "thejadepillar",
    profile_pic_url: "/images/jadepropic1.jpg",
  },

  maya: {
    id: 9002,
    name: "Maya Rodriguez",
    display_name: "Maya Rodriguez",
    username: "mayamoves",
    profile_pic_url: "/images/mayapropic2.png",
  },

  reeves: {
    id: 9003,
    name: "Reeves Li",
    display_name: "Reeves Li",
    username: "reevesli1225",
    profile_pic_url: "/images/reevespropic1.jpg",
  },

  aaliyah: {
    id: 9004,
    name: "Aaliyah Harris",
    display_name: "Aaliyah Harris",
    username: "aharris214",
    profile_pic_url: "/images/aaliyahpropic1.jpg",
  },

  dhruv: {
    id: 9005,
    name: "Dhruv Chatterjee",
    display_name: "Dhruv Chatterjee",
    username: "dhruvchat",
    profile_pic_url: "/images/dhruvpropic1.jpg",
  },

  yuna: {
    id: 9006,
    name: "Yuna Moon",
    display_name: "Yuna Moon",
    username: "yunamoon",
    profile_pic_url: "/images/yunapropic1.jpg",
  },
};

const DEMO_MEMBERS = [
  DEMO_USERS.jade,
  DEMO_USERS.yuna,
  DEMO_USERS.dhruv,
  DEMO_USERS.aaliyah,
  DEMO_USERS.maya,
  DEMO_USERS.reeves,
];

/* -----------------------------------------------------------
   DEMO ACTIVITIES
------------------------------------------------------------ */

const DEMO_ACTIVITIES = [
  {
    id: 7101,
    user_id: DEMO_USERS.dhruv.id,
    user: DEMO_USERS.dhruv,

    title: "Summer grill night in the Hamptons",

    description:
      "NY strips, ribeyes, and veggie skewers on the grill for dinner tonight. Tried Jamie Oliver's 'Dress the Board' technique by chopping parsley, garlic, rosemary, flaky salt, olive oil, and lemon zest directly on the cutting board before resting the steaks on top. The meat soaked up all those flavors and it honestly lived up to the hype.",

    mood: "Happy Camper",
    duration: "2 hours",
    location: "East Hampton, NY",
    timestamp: hoursAgo(2),

    tags: ["grilling", "steak", "summer"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/grill1.jpg",
      "/images/grill2.jpg",
    ],

    high_five_count: 24,
    has_high_fived: true,
    comment_count: 5,

    highfivers_preview: [
      DEMO_USERS.jade,
      DEMO_USERS.maya,
      DEMO_USERS.aaliyah,
    ],

    commenters_preview: [
      DEMO_USERS.yuna,
      DEMO_USERS.reeves,
    ],

    demo_comments: [
      {
        id: 9301,
        content:
          "The dress-the-board technique is such a hack",
        created_at: hoursAgo(1.4),
        star_count: 4,
        starred_by_me: false,
        user: DEMO_USERS.jade,
      },
      {
        id: 9302,
        content:
          "I feel fomo 😭",
        created_at: hoursAgo(0.8),
        star_count: 2,
        starred_by_me: true,
        user: DEMO_USERS.maya,
      },
    ],
  },
  {
    id: 7102,
    user_id: DEMO_USERS.maya.id,
    user: DEMO_USERS.maya,

    title: "Holiday cookie boxes",

    description:
      "Made Half Baked Harvest's gooey chocolate chip cookies for coworkers and friends. Added extra flaky sea salt on top. Hopefully enough survive the packaging process.",

    mood: "Festive",
    duration: "1 hour 30 minutes",
    location: "Lake Forest, IL",
    timestamp: hoursAgo(9),

    tags: ["baking", "cookies", "holiday"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/crinklecookies1.jpg",
    ],
    
    high_five_count: 34,
    has_high_fived: true,
    comment_count: 3,

    highfivers_preview: [
      DEMO_USERS.yuna,
      DEMO_USERS.jade,
      DEMO_USERS.reeves,
      DEMO_USERS.dhruv,
    ],

    commenters_preview: [
      DEMO_USERS.yuna,
      DEMO_USERS.reeves,
    ],

    demo_comments: [
      {
        id: 9303,
        content:
          "Yum!",
        created_at: hoursAgo(3.4),
        star_count: 1,
        starred_by_me: true,
        user: DEMO_USERS.dhruv,
      },
      {
        id: 9304,
        content:
          "These were so good!",
        created_at: hoursAgo(0.8),
        star_count: 2,
        starred_by_me: true,
        user: DEMO_USERS.yuna,
      },
    ],
  },
  {
    id: 7103,
    user_id: DEMO_USERS.jade.id,
    user: DEMO_USERS.jade,

    title: "Fresh pasta from scratch",

    description:
      "Took a pasta-making class at Williams Hearth today. Made fresh pasta dough by hand and paired it with a brown butter, sage, and crispy shallot sauce finished with Parmigiano Reggiano. Found myself leaving with a Peugeot automatic salt and pepper mills purchase.",

    mood: "Carb-loaded",
    duration: "2 hours",
    location: "Williams Hearth Test Kitchen, SoHo",
    timestamp: hoursAgo(27),

    tags: ["pasta", "cooking class", "homemade"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/pasta1.jpg",
      "/images/pasta2.jpg",
      "/images/pasta3.jpg",
    ],
  },
  {
    id: 7104,
    user_id: DEMO_USERS.yuna.id,
    user: DEMO_USERS.yuna,

    title: "Bulgogi for potluck",

    description:
      "Definitely not a traditional bulgogi since I couldn't find Asian pear and a few other ingredients, but it still turned out surprisingly good. Used grated kiwi to help tenderize the beef, which worked really well. Next time, will try Paik Jong-won's Youtube channel and make a trip to H Mart first!",

    mood: "Satisfied",
    duration: "1 hour",
    location: "Tribeca, NY",
    timestamp: hoursAgo(45),

    tags: ["koreanfood", "potluck", "bulgogi"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/bulgogi1.jpg",
    ],
  },
  {
    id: 7105,
    user_id: DEMO_USERS.aaliyah.id,
    user: DEMO_USERS.aaliyah,

    title: "Hosted Thanksgiving for the first time",

    description:
      "First turkey ever and somehow it actually came out juicy. Also somehow ended up grilling steaks and lamb because apparently one main course wasn't enough. Green beans and mashed potatoes rounded things out. Slightly concerned we may have overestimated how much meat six people could eat.",

    mood: "Accomplished",
    duration: "8 hours",
    location: "Greenwich, CT",
    timestamp: hoursAgo(68),

    tags: ["thanksgiving", "hosting", "holiday"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/turkey1.jpg",
      "/images/turkey2.jpg",
    ],
  },
  {
    id: 7106,
    user_id: DEMO_USERS.reeves.id,
    user: DEMO_USERS.reeves,

    title: "The easiest weeknight dinner",

    description:
      "Reeves' classic: Beyond Meat Italian sausages (yes, I'm not even vegan) tossed with Seggiano Organic Toscani pasta, garlic, olive oil, parmesan, and a little pasta water. Takes about twenty minutes, somehow tastes even better the next day, and I'll never stop buying that pasta.",

    mood: "Comforted",
    duration: "20 minutes",
    location: "Miami, FL",
    timestamp: hoursAgo(96),

    tags: ["weeknight dinner", "pasta", "easy meals"],

    circles: [{ id: 8102, name: "Home Cooks" }],

    media_urls: [
      "/images/homepasta1.jpg",
      "/images/homepasta2.jpg",
      "/images/homepasta3.jpg",
    ],
  }
];

/* -----------------------------------------------------------
   CIRCLE SLIM BANNER
------------------------------------------------------------ */

const CircleSlimPreviewBanner = () => {
  const tags = ["Cooking", "Food", "Recipes"];

  return (
    <section className="rounded-[30px] bg-blue-100 ring-4 ring-blue-400 px-4 py-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-4 sm:gap-6">
        <img
          src="/images/homecookscatpropic1.png"
          alt="Home Cooks"
          className="
            h-20 w-20 shrink-0
            rounded-full object-cover
            sm:h-28 sm:w-28
          "
        />

        <div className="min-w-0 flex-1">
          <h1
            className="
              truncate text-[16px] font-black
              tracking-[-0.03em] text-blue-700
              sm:text-[22px]
            "
          >
            Home Cooks
          </h1>

          <p className="mt-1 max-w-[390px] text-sm leading-snug text-gray-700 sm:max-w-xl">
            <span className="font-medium">
              "Anyone can cook." — Chef Gusteau, Ratatouille
            </span>
            <br /> Sharing weeknight experiments, family recipes, and recipes for cooks at every level
          </p>

          {/* Location */}
          <div className="mt-2 flex items-center gap-1 text-xs sm:text-sm text-gray-600">
            <MapPin
              size={13}
              weight="duotone"
              className="text-blue-700"
            />
            <span>New York City</span>
          </div>

          {/* Tags */}
          <div className="mt-1.5 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="
                  rounded-full
                  bg-white/70
                  px-2.5 py-0.5 
                  text-[11px]
                  font-semibold
                  text-blue-700
                "
              >
                {tag}
              </span>
            ))}
          </div>

          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 sm:text-xs">
            <Handshake
              size={13}
              weight="duotone"
              className="text-blue-600"
            />

            <span>
              In partnership with{" "}
              <span className="font-bold text-gray-700">
                Williams Hearth
              </span>
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   UPCOMING EVENT
------------------------------------------------------------ */

const UpcomingEventPanel = ({ onAction }) => {
  const attendees = [
    {
      id: 1,
      name: "Maya",
      profile_pic_url: "/images/mayapropic2.png",
    },
    {
      id: 2,
      name: "Aaliyah",
      profile_pic_url: "/images/aaliyahpropic1.jpg",
    },
    {
      id: 3,
      name: "Dhruv",
      profile_pic_url: "/images/dhruvpropic1.jpg",
    },
  ];

  return (
    <section className="rounded-2xl bg-white px-4 py-3.5">
      <div className="flex items-center justify-between">
        <h2 className="ml-1 text-lg font-semibold text-blue-600">
          Upcoming Events
        </h2>
      </div>

      <div
        className="
          mt-4 flex items-start gap-5
          rounded-3xl bg-blue-100
          px-3 pb-2 pt-3
        "
      >
        {/* Date tile */}
        <div
          className="
            w-14 shrink-0
            rounded-2xl bg-white
            px-3 pb-3 pt-2
            text-center
            shadow-[0px_4px_0px_rgba(37,99,235,0.96)]
          "
        >
          <div className="-mb-1.5 text-lg font-semibold text-amber-500">
            25
          </div>

          <div className="text-sm font-bold text-gray-500">
            JUL
          </div>
        </div>

        {/* Event content */}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-600">
            Sat 11:00 AM
          </p>

          <h3 className="break-words text-sm font-medium leading-tight text-gray-900">
            The Perfect Chocolate Chip Cookie
          </h3>

          <button
            type="button"
            className="
              mt-1 block
              !min-h-0 !min-w-0
              text-left text-[11.5px] text-blue-600
              hover:underline
              md:text-[11px]
            "
          >
            Williams Hearth Test Kitchen, SoHo
          </button>

          <p className="mt-1.5 leading-tight whitespace-pre-wrap text-[13px] text-gray-700">
            Discover the little techniques behind crisp edges, gooey centers,
            and bakery-worthy chocolate chip cookies. Perfect for beginners
            and home bakers alike!
          </p>

          {/* RSVP and attendees */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="
                inline-flex h-7
                !min-h-0 !min-w-0
                items-center justify-center
                overflow-hidden rounded-full
                bg-blue-700 px-3 py-1.5
                text-[13px] leading-none text-white
                transition hover:bg-blue-800
              "
            >
              Let's do it!
            </button>

            <button
              type="button"
              className="
                flex h-7
                !min-h-0 !min-w-0
                items-center justify-center
                overflow-hidden
              "
              aria-label="View attendees"
            >
              <AvatarStack users={attendees} />
            </button>

            <span className="text-[11px] font-medium text-gray-500">
              14 going
            </span>
          </div>

          <button
            type="button"
            className="
              mt-2
              !min-h-0 !min-w-0
              text-xs text-blue-700
              hover:underline
            "
          >
            View event
          </button>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   UPLOAD ACTIVITY
------------------------------------------------------------ */

const UploadActivityButton = ({ onAction }) => {
  return (
    <button
      type="button"
      onClick={onAction}
      className="
        w-full
        !min-h-0 !min-w-0
        rounded-3xl bg-blue-600
        px-3 sm:py-2.5 py-2 sm:mb-0 mb-3
        font-semibold text-white
        transition hover:bg-blue-700
      "
    >
      Upload Activity
    </button>
  );
};

/* -----------------------------------------------------------
   MEMBERS PANEL
------------------------------------------------------------ */

const MembersPanel = ({ onAction }) => {
  return (
    <section className="rounded-2xl bg-white p-5 pb-3 pt-3">
      <h3 className="mb-2 text-lg font-semibold text-gray-900">
        Members
      </h3>

      <div className="flex -space-x-3">
        {DEMO_MEMBERS.slice(0, 4).map((member) => (
          <img
            key={member.id}
            src={member.profile_pic_url}
            alt={member.display_name}
            title={member.display_name}
            className="
              h-9 w-9 rounded-full
              border-2 border-white object-cover
            "
          />
        ))}
      </div>

      <button
        type="button"
        className="
          mt-2 ml-1
          !min-h-0 !min-w-0
          bg-transparent p-0
          text-sm text-gray-900
          transition
          hover:font-medium hover:text-blue-600
        "
      >
        248 members
      </button>
    </section>
  );
};

/* -----------------------------------------------------------
   PROGRESS PANEL
------------------------------------------------------------ */

const CircleProgressPanel = () => {
  const items = [
    {
      icon: CalendarBlank,
      label: "Monthly Calendar",
      value: "12 days",
    },
    {
      icon: Fire,
      label: "Current Streak",
      value: "6 days",
    },
    {
      icon: Target,
      label: "July Goals",
      value: "3 of 5",
    },
    {
      icon: CheckCircle,
      label: "Weekly Plan",
      value: "On track",
    },
  ];

  return (
    <section className="mt-4 rounded-2xl bg-white p-5">
      <p className="mb-1 px-1 text-[16px] font-black tracking-[-0.02em] text-blue-600">
        Your Progress
      </p>

      <div className="space-y-1">
        {items.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-xl px-2 py-2.5 font-semibold"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Icon
                size={17}
                weight="duotone"
                className="shrink-0 text-blue-600"
              />

              <span className="truncate text-sm text-gray-700">
                {label}
              </span>
            </div>

            <span className="ml-2 shrink-0 text-sm font-medium text-gray-500">
              {value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   ANNOUNCEMENT
------------------------------------------------------------ */

const AnnouncementBanner = ({ onAction }) => {
  return (
    <section className="relative rounded-2xl bg-white p-5 pb-3 pt-3">
      {/* Floating pill */}
      <div className="absolute -top-3 left-5">
        <span
          className="
            inline-flex items-center gap-1.5
            rounded-full bg-blue-600
            px-3 py-1
            text-sm text-white
            shadow-sm
          "
        >
          <Megaphone size={14} weight="fill" className="text-white" />
          Announcements
        </span>
      </div>

      {/* Header buttons */}
      <div className="mt-1 sm:mb-0 mb-2 flex items-center justify-end">
        <div className="flex gap-1.5">
          <button
            type="button"
            className="
              inline-flex h-6
              !min-h-0 !min-w-0
              items-center justify-center
              overflow-hidden rounded-full
              bg-blue-100 px-3 py-1
              text-sm leading-none text-blue-700
              transition-colors
              hover:ring-2 hover:ring-blue-200
            "
            style={{ lineHeight: 1 }}
          >
            View all
          </button>

          <button
            type="button"
            className="
              inline-flex h-6
              !min-h-0 !min-w-0
              items-center justify-center
              overflow-hidden rounded-full
              bg-yellow-200 px-3 py-1
              text-sm leading-none text-black
              transition-colors
              hover:ring-2 hover:ring-yellow-300
            "
            style={{ lineHeight: 1 }}
          >
            Create
          </button>
        </div>
      </div>

      {/* Announcement content */}
      <button
        type="button"
        className="
          mt-0 block w-full
          !min-h-0 !min-w-0
          text-left
        "
      >
        <div className="break-words text-lg font-semibold leading-snug text-gray-900 md:text-[15px]">
          🥣 &nbsp;Willams Hearth x Isla Garden
        </div>

        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-snug text-gray-700">
          Join chef Isla Garden for a month-long
          celebration of cozy winter flavors. Each week, she shares an
          exclusive new recipe only on Third Place. Cook along, share your
          results, and get personal notes from Isla.
        </p>
      </button>
    </section>
  );
};

/* -----------------------------------------------------------
   CIRCLE STATUS PANEL
------------------------------------------------------------ */

const CircleStatusPanel = ({ onAction }) => {
  return (
    <section className="rounded-2xl items-end bg-transparent px-3 sm:mb-0 -mb-1 sm:pt-3 pt-2">
      <div className="flex flex-wrap items-start justify-end gap-3 md:justify-start">
        <span
          className="
            inline-flex items-center gap-1
            rounded-full bg-gray-100
            !min-h-0 !min-w-0 !px-2 !py-0.5
            text-xs font-medium text-gray-700
          "
        >
          <LockSimpleOpen size={14} />
          Public
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 md:justify-start">
        <button
          type="button"
          className="
            !min-h-0 !min-w-0
            rounded-full bg-blue-600
            sm:!px-3 sm:!py-1 px-2.5 py-1.5
            text-xs sm:text-sm leading-none text-white
            transition
            hover:ring-2 hover:ring-blue-400
          "
        >
          Invite
        </button>

        <button
          type="button"
          className="
            !min-h-0 !min-w-0
            rounded-full bg-blue-800
            sm:!px-3 sm:!py-1 px-2.5 py-1.5
            text-xs sm:text-sm font-medium leading-none text-white
            transition
            hover:ring-2 hover:ring-blue-400
          "
        >
          Join
        </button>
      </div>
    </section>
  );
};


/* -----------------------------------------------------------
   CIRCLE DETAILS PREVIEW PAGE
------------------------------------------------------------ */

function ExploreCircleDetailsPreview({ embedded = false }) {
  const navigate = useNavigate();

  const handlePreviewAction = () => {
    navigate("/signup");
  };

  return (
    <div
      className={
        embedded
          ? "bg-blue-50 text-[#181411]"
          : "min-h-screen bg-blue-50 text-[#181411]"
      }
    >
      {!embedded && <PreLoginHeader />}

      {!embedded && (
        <section className="px-6 pb-6 pt-[4.45rem] text-center md:px-10 lg:pb-8 lg:pt-24">
          <h1 className="mx-auto max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 lg:text-[38px]">
            Your {" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>
            {" "} starts here
          </h1>

          <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight sm:tracking-[-0.02em] text-gray-600 sm:text-[20px]">
            Share progress, organize online and offline events,
            <br className="sm:hidden" />
            <br className="hidden sm:block" />
            {" "}celebrate every step,
            <br className="sm:hidden" />
            {" "}and turn hobbies into real community
          </p>
        </section>
      )}

      {/* Circle slim banner */}
      <div className="w-full bg-blue-50">
        <div className="mx-auto w-full max-w-[400px] sm:max-w-7xl px-5 sm:pb-4 pb-2 sm:px-6 md:px-10 xl:px-16">
          <CircleSlimPreviewBanner />
        </div>
      </div>

      {/* Circle page layout */}
      <main
        className="
          mx-auto w-full max-w-7xl
          px-4 pb-2
          sm:px-6 md:px-10 xl:px-16
        "
      >
        <div
          className="
            grid grid-cols-1 items-start gap-6
            md:grid-cols-[1fr_minmax(0,2fr)]
            min-[1100px]:grid-cols-[1fr_minmax(0,2fr)_1fr]
          "
        >
          {/* LEFT RAIL */}
          <aside className="hidden md:sticky md:top-4 md:block md:h-max">
            <div
              className="
                space-y-4 overflow-auto
                overscroll-contain no-scrollbar
              "
              style={{
                maxHeight: "calc(100dvh - 80px)",
              }}
            >
              <UpcomingEventPanel onAction={handlePreviewAction} />

              <CircleProgressPanel onAction={handlePreviewAction} />
            </div>
          </aside>

          {/* MIDDLE */}
          <section className="min-w-0 space-y-4">
            {/* Mobile status */}
            <div className="md:hidden">
              <CircleStatusPanel onAction={handlePreviewAction} />
            </div>

            {/* Mobile event */}
            <div className="md:hidden">
              <UpcomingEventPanel onAction={handlePreviewAction} />
            </div>

            {/* Mobile members */}
            <div className="md:hidden">
              <MembersPanel onAction={handlePreviewAction} />
            </div>

            {/* Mobile upload */}
            <div className="md:hidden">
              <UploadActivityButton onAction={handlePreviewAction} />
            </div>

            <AnnouncementBanner onAction={handlePreviewAction} />

            <div
              className="
                space-y-4
                md:max-h-[calc(100dvh-180px)]
                md:overflow-auto
                md:pb-24 md:pr-1
                no-scrollbar
              "
            >
              {DEMO_ACTIVITIES.map((activity, index) => (
                <div
                  key={activity.id}
                  className="relative"
                  style={index === 0 ? { marginTop: "1.5rem" } : undefined}
                >
                  {index === 0 && (
                    <span
                      className="
                        absolute -top-2 left-3 z-10
                        inline-flex items-center gap-1
                        rounded-full bg-sky-200
                        px-2 py-0.5
                        text-xs font-medium text-blue-800
                        shadow-[0_2px_0px_rgba(37,99,235,0.96)]
                      "
                    >
                      <PushPin size={14} weight="fill" />
                      Pinned
                    </span>
                  )}

                  <ActivityCard
                    activity={activity}
                    currentUser={DEMO_USERS.jade}
                    readOnly
                    contentClassName="px-6 py-5"
                    onTagClick={handlePreviewAction}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* RIGHT RAIL */}
          <aside className="hidden min-[1100px]:sticky min-[1100px]:top-4 min-[1100px]:block min-[1100px]:h-max">
            <div
              className="
                space-y-4 overflow-auto
                overscroll-contain pr-1 no-scrollbar
              "
              style={{
                maxHeight: "calc(100dvh - 80px)",
              }}
            >
              <CircleStatusPanel onAction={handlePreviewAction} />

              <MembersPanel onAction={handlePreviewAction} />

              <UploadActivityButton onAction={handlePreviewAction}/>
            </div>
          </aside>
        </div>
      </main>
      
      <section
        id="about-preview"
        className={`scroll-mt-20 mt-3 bg-blue-50 pt-8 sm:pt-14 ${
          embedded ? "" : ""
        }`}
      >
        <div className="px-6 pb-2 text-center sm:pb-6 md:px-10">
          <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-blue-700">
            About Third Place
          </span>

          <h2 className="mx-auto mt-3 max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 sm:text-3xl lg:text-[38px]">
            "
            <span className="whitespace-nowrap text-blue-500">
              GOOOOOD JOB!
            </span>
            "
          </h2>
        </div>

        <AboutPage embedded />
      </section>

    </div>
  );
}

export default ExploreCircleDetailsPreview;